import { useState, useEffect } from "react";
import { getProcurementID } from "../../Constants";
import { Urls } from "../../../Urls";
import axios from "../../../Axios";

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default function useAiChat({ userData, location, history, replyTo }) {
  const [chatHistory, setChatHistory] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [allowedPrompts, setAllowedPrompts] = useState(0);

  const procurement_id = getProcurementID();
  const isUserInAuction = ["adminconsole", "history"].some((k) =>
    location?.pathname?.includes(k)
  );

  // On load: set allowed prompts
  useEffect(() => {
    const allowed = userData?.policy?.lorri_ai?.allowed_prompts || 0;
    setAllowedPrompts(allowed);
  }, [userData]);

  // Fetch session once on mount
  useEffect(() => {
    fetchSession();
  }, []);

  const fetchSession = () => {
    axios
      .get(`${Urls.auctionUrl}/intelligence?field=fetch_session`)
      .then((res) => {
        setChatHistory(res.data.messages || []);
        setSessionId(res.data.session_id);
      })
      .catch((err) => {
        console.error("Error fetching session:", err);
      });
  };

  const resetChat = () => {
    axios
      .post(`${Urls.auctionUrl}/intelligence?field=create_session`)
      .then((res) => {
        setChatHistory(res.data.data.messages || []);
        setSessionId(res.data.data.session_id || "");
      })
      .catch((err) => {
        console.error("Error resetting session:", err);
      });
  };

  const sendPrompt = async (promptText) => {
    if (allowedPrompts === 0) {
      alert("You have reached the maximum number of prompts allowed.");
      return;
    }

    const prompt = promptText?.trim();
    if (!prompt) return;

    if (prompt.length > 1000) {
      const errorEntry = {
        id: generateId(),
        timestamp: Date.now(),
        prompt,
        response: `Your message exceeds the maximum limit.`,
        isError: true,
      };
      setChatHistory((prev) => [...prev, errorEntry]);
      return;
    }

    const entryId = generateId();
    const newEntry = {
      id: entryId,
      timestamp: Date.now(),
      prompt,
      response: null,
      reply: replyTo || "",
    };

    setChatHistory((prev) => [...prev, newEntry]);

    const resolvedProcId =
      history?.location?.state?.procurement_id ??
      history?.location?.state?.details?.procurement_id ??
      procurement_id;

    let url = `${Urls.auctionUrl}/intelligence?field=ask&session_id=${sessionId}`;
    if (resolvedProcId) url += `&procurement_id=${resolvedProcId}`;

    const payload = isUserInAuction
      ? { query: prompt, reply: replyTo, auction_id: procurement_id }
      : { query: prompt, reply: replyTo };

    try {
      const res = await axios.post(url, payload);
      const responseText = res.data?.response || "No response received.";
      setChatHistory((prev) =>
        prev.map((entry) =>
          entry.id === entryId ? { ...entry, response: responseText } : entry
        )
      );
      setAllowedPrompts((prev) => prev - 1);
    } catch (err) {
      console.error("AI error:", err);
      setChatHistory((prev) =>
        prev.map((entry) =>
          entry.id === entryId
            ? {
                ...entry,
                response: "Something went wrong. Please try again.",
                isError: true,
              }
            : entry
        )
      );
    }
  };

  return {
    chatHistory,
    allowedPrompts,
    sendPrompt,
    resetChat,
    fetchSession,
    setChatHistory,
  };
}
